import React, { useState, useEffect, useRef } from 'react';
import { Calendar, TrendingUp, FileText, Package, BarChart3, Settings, Target, Users, AlertTriangle, Download, Loader2, RefreshCw, X, MoreVertical, Edit, Trash2, CheckCircle } from 'lucide-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import CurrentClientContext from '../../providers/CurrentClientProvider';
import AuthContext from '../../providers/AuthProvider';
import constants from '../../constants/constant';
import { getsVendorTopSellersReports } from './vendorTopSellersAccess';
import { Visibility } from '@mui/icons-material';
import axios from 'axios';
import { trackEvent } from '../../services/AmplitudeService';
import { ProgressDashboardTableReportModal } from '../../components/reports/progressDashboard/ProgressDashboardTableReport.component';
import EpochMonthService from '../../services/EpochMonth.service';
import { ClassPerformanceReport } from '../../components/reports/classPerformance/ClassPerformanceReport.component';
import ReportModal from '../../components/reports/ReportModal.component';
import { BuyerPerformanceReportModal } from '../../components/reports/buyerPerformance/BuyerPerformanceTableReport.component';
import { InventoryPerformanceReportModal } from '../../components/reports/inventoryPerformance/InventoryPerformanceTableReport.component';
import { InventoryVarianceReportModal } from '../../components/reports/inventoryVariance/InventoryVarianceTableReport.component';
import { InventoryActionReportModal } from '../../components/reports/inventoryAction/InventoryActionTableReport.component';
import { GlobalAnalyticsReportModal } from '../../components/reports/globalAnalytics/GlobalAnalyticsReport.component';
import { OtbRecapTableReportModal } from '../../components/reports/otbRecap/OtbRecapTableReport.component';
import KpiTable from '../../components/client/KpiTable.component';
import ClientDataService from '../../services/ClientData.service';
// Import custom report components
import MRFReport from '../../components/reports/custom/MRFReport.component';
import CustomGoalsReport from '../../components/reports/custom/CustomGoalsReport.component';
import MKECustomReport from '../../components/reports/custom/MKECustomReport.component';
import SaintBernardExport from '../../components/reports/custom/SaintBernardExport.component';
import YoungworldOtbReport from '../../components/reports/youngWorld/YoungworldOtbReport.component';
import YoungworldNextQuarterReport from '../../components/reports/youngWorld/YoungworldNextQuarterReport.component';
import YoungworldWeeklyReport from '../../components/reports/youngWorld/YoungworldWeeklyReport.component';
import MonthlyExportReport from '../../components/reports/MonthlyExportReport.component';
import { DataValidationReport } from '../../components/reports/DataValidationReport.component';
import TootsiesClassPerformanceReport from '../../components/reports/custom/Tootsies/TootsiesClassPerformanceReport.component';
import TootsiesGlobalAnalyticsReport from '../../components/reports/custom/Tootsies/TootsiesGlobalAnalyticsReport.component';
import TootsiesInventoryPerformanceReport from '../../components/reports/custom/Tootsies/TootsiesInventoryPerformanceReport.component';
import WholeEarthCustomReport from '../../components/reports/custom/WholeEarthCustomReport.component';
import VendorPerformanceReport from '../../components/reports/vendorPerformance/VendorPerformanceReport.component';
import TopSellersReport from '../../components/reports/topSellers/TopSellersReport.component';
import AgedInventoryReport from '../../components/reports/agedInventory/AgedInventoryReport.component';
import CoreReplenishmentReport from '../../components/reports/coreReplenishment/CoreReplenishmentReport.component';
import {
    buildClientCustomReportCreatePath,
    buildClientCustomReportEditPath,
    isV3ClientDashboardPath,
} from '../../services/ClientDashboardV3';

const TODAY_EPOCH = EpochMonthService.getTodayEpochMonth();

// Helps verify which bundle is deployed in a given environment (GCP vs local)
const BUILD_INFO = {
    sha: process.env.REACT_APP_GIT_SHA || process.env.REACT_APP_COMMIT_SHA || 'unknown',
    builtAt: process.env.REACT_APP_BUILD_TIME || 'unknown',
};

const DynamicModalContents = ({ reportTitle, clientId, onClose, monthVersion, epochMonth, componentName }) => {
    // If componentName is provided (for custom reports), use it to render the appropriate component
    if (componentName) {
        console.log(componentName);
        const customComponentMap = {
            'MRFReport': MRFReport,
            'CustomGoalsReport': CustomGoalsReport,
            'MKECustomReport': MKECustomReport,
            'SaintBernardExport': SaintBernardExport,
            'YoungworldOtbReport': YoungworldOtbReport,
            'YoungworldNextQuarterReport': YoungworldNextQuarterReport,
            'YoungworldWeeklyReport': YoungworldWeeklyReport,
            'TootsiesBrandClassReport': TootsiesClassPerformanceReport,
            'TootsiesBrandGlobalReport': TootsiesGlobalAnalyticsReport,
            'TootsiesBrandInventoryReport': TootsiesInventoryPerformanceReport,
            'WholeEarthCustomReport': WholeEarthCustomReport,
            'VendorPerformanceReport': VendorPerformanceReport,
            'VendorPerformance': VendorPerformanceReport,
            'TopSellersReport': TopSellersReport,
            'TopSellers': TopSellersReport,
            'AgedInventoryReport': AgedInventoryReport,
            'AgedInventory': AgedInventoryReport,
            'CoreReplenishmentReport': CoreReplenishmentReport,
            'CoreReplenishment': CoreReplenishmentReport,
        };

        const CustomComponent = customComponentMap[componentName];
        if (CustomComponent) {
            // Some components may not require epochMonth (like SaintBernardExport)
            return <CustomComponent clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        }
    }

    // Default report rendering based on title
    switch (reportTitle) {
        case 'Progress':
            return <ProgressDashboardTableReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Class Performance':
            return <ClassPerformanceReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Inventory Performance':
            return <InventoryPerformanceReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Buyer Performance':
            return <BuyerPerformanceReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Inventory Variance':
            return <InventoryVarianceReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Inventory Action':
            return <InventoryActionReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Global Analytics':
            return <GlobalAnalyticsReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'OTB Recap':
            return <OtbRecapTableReportModal clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} monthVersion={monthVersion} />;
        case 'KPI Report':
            return <KpiTable clientId={clientId} />;
        case 'Monthly Export':
            return <MonthlyExportReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} />;
        case 'Data Validation':
            return <DataValidationReport clientId={clientId} />;
        case 'Vendor Performance Analysis':
        case 'Vendor Performance':
            return <VendorPerformanceReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Top Sellers / Item Performance':
        case 'Top Sellers':
            return <TopSellersReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Aged Inventory':
            return <AgedInventoryReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        case 'Core Item Replenishment':
            return <CoreReplenishmentReport clientId={clientId} epochMonth={epochMonth ?? TODAY_EPOCH} onClose={onClose} />;
        default:
            console.error(`No matching report component for title: ${reportTitle}`);
            return null;
    }
};

export default function ReportControlsDashboard() {
    const [activeTab, setActiveTab] = useState('weekly');
    const [downloadingReports, setDownloadingReports] = useState({});
    const [reportStatuses, setReportStatuses] = useState({}); // Track last_report_file for each report
    const [customReports, setCustomReports] = useState([]);
    const [defaultFilters, setDefaultFilters] = useState({}); // Store filter IDs for default reports
    const [loadingCustomReports, setLoadingCustomReports] = useState(false);
    const [loadingDefaultFilters, setLoadingDefaultFilters] = useState(false);
    const [updatingFilters, setUpdatingFilters] = useState(false); // Track when filters are being updated with control changes
    const [isAdmin, setIsAdmin] = useState(false);
    const [missingFiltersCount, setMissingFiltersCount] = useState(0); // Track how many reports are missing filters
    const [openMenuId, setOpenMenuId] = useState(null); // Track which dropdown menu is open
    const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, filterId: null, filterName: '' }); // Delete confirmation modal
    const [refreshCustomReports, setRefreshCustomReports] = useState(0); // Trigger to refresh custom reports

    // New state for report templates from database
    const [weeklyReports, setWeeklyReports] = useState([]);
    const [monthlyReports, setMonthlyReports] = useState([]);
    const [loadingReportTemplates, setLoadingReportTemplates] = useState(false);

    // State for OTB Recap month version (EOM or MTD)
    const [otbRecapMonthVersion, setOtbRecapMonthVersion] = useState('MONTH_TO_DATE');

    // Data Through Date state
    const [dataThroughDate, setDataThroughDate] = useState(null);

    const { clientId, client_code } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const client_context = CurrentClientContext.useClient();
    const auth_context = AuthContext.useAuth();
    const calendarRef = useRef(null);
    const previousClientIdRef = useRef(null); // Track previous client ID to detect switches
    const pendingAutoDownloadsRef = useRef({});

    // Dynamic View Modal State
    const [showDynamicModal, setShowDynamicModal] = useState(false);
    const [dynamicReportTitle, setDynamicReportTitle] = useState(null);
    const [dynamicReportComponentName, setDynamicReportComponentName] = useState(null);
    const isDashboardV3View = isV3ClientDashboardPath(location.pathname);

    // Helper to open dynamic modal for a report
    const openDynamicModal = (reportTitle, componentName = null) => {
        setDynamicReportTitle(reportTitle);
        setDynamicReportComponentName(componentName);
        setShowDynamicModal(true);
    };

    // Helper to close dynamic modal
    const closeDynamicModal = () => {
        setShowDynamicModal(false);
        setDynamicReportTitle(null);
        setDynamicReportComponentName(null);
    };

    // Control states
    const [reportPeriod, setReportPeriod] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
    const [startingPeriod, setStartingPeriod] = useState(new Date().getMonth()); // Default to current month (October = 9)
    const [endingPeriod, setEndingPeriod] = useState((new Date().getMonth() + 11) % 12); // 11 months forward (September = 8)
    const [viewMode, setViewMode] = useState('Cost'); // Default to Cost
    const [sortBy, setSortBy] = useState('CLASS'); // CLASS, EXECUTIVE, LOCATION
    const [showCalendar, setShowCalendar] = useState(false);

    const activeClientId = client_context.client?.Id || clientId;
    const activeClientIdRef = useRef(activeClientId);
    activeClientIdRef.current = activeClientId;
    const customReportsRequestRef = useRef(0);

    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Report control configurations based on requirements
    const reportControlConfig = {
        'otb_recap': {
            adjustableStart: true,
            adjustableEnd: true,
            adjustableRetailCost: true,
            adjustableTrend: true,
            reportName: 'otb_recap'
        },
        'class_performance': {
            adjustableStart: true,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'class_performance'
        },
        'inventory_performance': {
            adjustableStart: true,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'inventory_performance'
        },
        'buyer_performance': {
            adjustableStart: true,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'buyer_performance'
        },
        'inventory_variance': {
            adjustableStart: true,
            adjustableEnd: false,
            adjustableRetailCost: true,
            adjustableTrend: false,
            reportName: 'inventory_variance'
        },
        'kpi_report': {
            adjustableStart: true,
            adjustableEnd: true,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'kpi_report'
        },
        'global_analytics': {
            adjustableStart: true,
            adjustableEnd: true,
            adjustableRetailCost: true,
            adjustableTrend: false,
            reportName: 'global_report'
        },
        'inventory_action': {
            adjustableStart: true,
            adjustableEnd: true,
            adjustableRetailCost: true,
            adjustableTrend: true,
            reportName: 'inventory_report'
        },
        'progress': {
            adjustableStart: false,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: true,
            reportName: 'progress'
        },
        'pod_report': {
            adjustableStart: false,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'pod_report'
        },
        'merchandise_plan': {
            adjustableStart: false,
            adjustableEnd: false,
            adjustableRetailCost: false,
            adjustableTrend: false,
            reportName: 'merchandise_report'
        }
    };

    const getMonthYearDisplay = () => {
        return `${months[reportPeriod.month]} ${reportPeriod.year}`;
    };

    const getReportPeriodFormatted = () => {
        // Format as MM/YYYY for backend
        const month = String(reportPeriod.month + 1).padStart(2, '0');
        return `${month}/${reportPeriod.year}`;
    };

    const handleMonthSelect = (monthIndex) => {
        setReportPeriod(prev => ({ ...prev, month: monthIndex }));
        setShowCalendar(false);
    };

    const handleYearChange = (direction) => {
        setReportPeriod(prev => ({
            ...prev,
            year: prev.year + (direction === 'up' ? 1 : -1)
        }));
    };

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };

        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    useEffect(() => {
        document.title = 'Retail ORBIT: Reports Library';
    }, []);

    // Check if user is admin
    useEffect(() => {
        // role-create marks admin, but we don't have role specific checks currently
        if (auth_context.checkPermission && auth_context.checkPermission('role-create')) {
            setIsAdmin(true);
        }
    }, [auth_context]);

    // Load data through date when client changes
    useEffect(() => {
        const loadDataThrough = async () => {
            try {
                if (!activeClientId) return;
                const requestClientId = activeClientId;
                const resp = await ClientDataService.getDataThroughDate({ clientId: activeClientId });
                if (activeClientIdRef.current !== requestClientId) {
                    return;
                }
                const payload = resp?.data || resp;
                const formatted = payload?.formatted || null;
                setDataThroughDate(formatted);
            } catch (e) {
                console.warn('Unable to load Data Through Date', e);
                setDataThroughDate(null);
            }
        };
        loadDataThrough();
    }, [activeClientId]);

    // Load default filters when client/user is available
    useEffect(() => {
        if (activeClientId && auth_context.user?.id && (activeTab === 'weekly' || activeTab === 'monthly')) {
            // Clear old filters and statuses when client changes to prevent showing stale data
            setDefaultFilters({});
            setReportStatuses({});
            loadDefaultFilters();
        }
    }, [activeClientId, auth_context.user, activeTab]);

    // Load custom reports from backend - load on initial mount to populate counter
    useEffect(() => {
        if (activeClientId) {
            // Clear old custom reports when client changes
            setCustomReports([]);
            loadCustomReports();
        }
    }, [activeClientId, auth_context.user?.id, refreshCustomReports]);

    // When controls change, update the filters for affected reports
    useEffect(() => {
        if (activeClientId && auth_context.user?.id && Object.keys(defaultFilters).length > 0) {
            updateFiltersWithControls();
        }
    }, [reportPeriod, startingPeriod, endingPeriod, viewMode, sortBy, otbRecapMonthVersion]);

    // When filters are loaded for a NEW client, clone them with current control settings
    // This ensures the filters belong to the correct client
    useEffect(() => {
        const hasFilters = Object.keys(defaultFilters).length > 0;
        const clientChanged = previousClientIdRef.current !== null && previousClientIdRef.current !== activeClientId;

        if (clientChanged) {
            // IMMEDIATELY clear report statuses when client changes
            // This prevents showing stale green icons from the previous client
            console.log(`🔄 Client changed from ${previousClientIdRef.current} to ${activeClientId}, clearing report statuses...`);
            setReportStatuses({});
        }

        if (hasFilters && clientChanged && !loadingDefaultFilters) {
            console.log(`🔄 Client changed, updating filters for client: ${activeClientId}`);
            updateFiltersWithControls();
        }

        // Update the ref to track current client
        previousClientIdRef.current = activeClientId;
    }, [defaultFilters, activeClientId, loadingDefaultFilters]);

    // Load report templates from database. Reloads when the client changes so
    // the catalog reflects that client's enabled reports.
    useEffect(() => {
        loadReportTemplates();
    }, [activeClientId]);

    // Auto-open specific report modal if specified in location state
    useEffect(() => {
        if (location.state?.openReport === 'inventory-action') {
            // Small delay to ensure component is fully mounted
            setTimeout(() => {
                openDynamicModal('Inventory Action');
                // Clear the state to prevent re-opening on subsequent renders
                navigate(location.pathname, { replace: true, state: {} });
            }, 100);
        }
    }, [location.state]);

    // Calculate missing filters count when reports or filters change
    useEffect(() => {
        if (!loadingReportTemplates && !loadingDefaultFilters) {
            const allReports = [...weeklyReports, ...monthlyReports];
            let missing = 0;

            allReports.forEach(report => {
                if (report.template?.has_pdf_download) {
                    const hasFilter = report.key && defaultFilters[report.key]?.filterId;
                    if (!hasFilter) {
                        missing++;
                        console.log(`Report "${report.title}" (${report.key}) is missing a filter`);
                    }
                }
            });

            setMissingFiltersCount(missing);

            if (missing > 0) {
                console.warn(`${missing} report(s) are missing default filters. PDF downloads will not be available for these reports.`);
            }
        }
    }, [weeklyReports, monthlyReports, defaultFilters, loadingReportTemplates, loadingDefaultFilters]);

    // Continuous polling for reports in BUILDING status
    useEffect(() => {
        // Find all reports that are currently building
        const buildingReports = Object.entries(reportStatuses).filter(
            ([key, status]) => status && (status.status === 'BUILDING' || status.status === 'NOT_GENERATED')
        );

        if (buildingReports.length === 0) {
            // No reports building, no need to poll
            return;
        }

        console.log(`Polling for ${buildingReports.length} report(s) in progress...`);
        const pollingClientId = activeClientId;

        // Set up polling interval
        const pollInterval = setInterval(async () => {
            // Check status for each building report
            for (const [statusKey, status] of buildingReports) {
                try {
                    if (activeClientIdRef.current !== pollingClientId) {
                        return;
                    }

                    // Extract filterId from statusKey
                    let filterId;
                    if (statusKey.startsWith('custom-')) {
                        // Custom report
                        filterId = statusKey.replace('custom-', '');
                    } else {
                        // Default report - need to find filter ID
                        const reportKey = statusKey;
                        const filterData = defaultFilters[reportKey];
                        filterId = filterData?.filterId;
                    }

                    if (!filterId) {
                        console.warn(`No filter ID found for ${statusKey}, skipping poll`);
                        continue;
                    }

                    // Check current status from API
                    const response = await axios.get(
                        `${constants.BACKEND_DOMAIN}/api/filters/${filterId}/last-report-file`
                    );

                    if (activeClientIdRef.current !== pollingClientId) {
                        return;
                    }

                    if (response.data && response.data.status === 'GENERATED') {
                        // PDF is ready, update status to show green icon
                        setReportStatuses(prev => ({
                            ...prev,
                            [statusKey]: response.data
                        }));
                        console.log(`✅ Report ${statusKey} completed generation (filter: ${filterId})`);
                    } else if (!response.data) {
                        console.log(`⏳ Report ${statusKey} has no report-file payload yet; keeping BUILDING state`);
                    } else if (response.data && response.data.status === 'FAILED') {
                        // Generation failed, remove building status
                        setReportStatuses(prev => {
                            const newState = { ...prev };
                            delete newState[statusKey];
                            return newState;
                        });
                        console.error(`❌ Report ${statusKey} generation failed`);
                    } else if (response.data && response.data.status === 'BUILDING') {
                        console.log(`⏳ Report ${statusKey} still building...`);
                    }
                    // If still BUILDING, leave it and check again next interval
                } catch (error) {
                    console.error(`Error checking status for ${statusKey}:`, error);
                }
            }
        }, 5000); // Poll every 5 seconds

        // Cleanup interval on unmount or when buildingReports changes
        return () => {
            console.log('Stopping poll interval');
            clearInterval(pollInterval);
        };
    }, [reportStatuses, defaultFilters]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Check if click is outside of any dropdown menu
            if (openMenuId && !event.target.closest('.dropdown-menu-container')) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);

    // Check for success message and tab state from location
    useEffect(() => {
        let shouldReloadCustomReports = false;

        // Switch to custom tab if specified (even without message)
        if (location.state?.tab === 'custom') {
            setActiveTab('custom');
            shouldReloadCustomReports = true;
        }

        // Show success message if present
        if (location.state?.message) {
            // Could add toast notification here
            console.log(location.state.message);
            shouldReloadCustomReports = true;
        }

        // Reload custom reports when returning from create/edit page
        if (shouldReloadCustomReports && activeClientId && auth_context.user?.id) {
            console.log('🔄 Reloading custom reports after create/edit...');
            // Trigger refresh by incrementing the counter
            setRefreshCustomReports(prev => prev + 1);
        }

        // Clear the state from history (only if there was a message or tab state)
        if (location.state?.message || location.state?.tab) {
            window.history.replaceState({}, document.title);
        }
    }, [location, activeClientId, auth_context.user]);

    const loadReportTemplates = async () => {
        setLoadingReportTemplates(true);
        try {
            // client_id scopes the catalog to what support has left enabled for
            // this client in Admin Tools -> Reports.
            const response = await axios.get(`${constants.BACKEND_DOMAIN}/api/report-templates`, {
                params: activeClientId ? { client_id: activeClientId } : undefined,
            });

            if (response.data && response.data.templates) {
                const templates = response.data.templates;

                // Map icon names to actual icon components
                const iconMap = {
                    'TrendingUp': TrendingUp,
                    'FileText': FileText,
                    'Package': Package,
                    'BarChart3': BarChart3,
                    'Target': Target,
                    'Users': Users,
                    'AlertTriangle': AlertTriangle,
                    'Settings': Settings,
                };

                // Separate templates by type and format for UI
                const weekly = templates
                    .filter(t => t.report_type === 'weekly')
                    .map(t => ({
                        title: t.title,
                        key: t.report_key,
                        label: t.label,
                        description: t.description,
                        icon: iconMap[t.icon] || TrendingUp,
                        lastUpdated: new Date(t.updated_at).toLocaleString(),
                        appliedControls: getAppliedControlsFromTemplate(t),
                        // Store additional template data
                        template: t
                    }));

                const monthly = templates
                    .filter(t => t.report_type === 'monthly')
                    .map(t => ({
                        title: t.title,
                        key: t.report_key,
                        label: t.label,
                        description: t.description,
                        icon: iconMap[t.icon] || TrendingUp,
                        lastUpdated: new Date(t.updated_at).toLocaleString(),
                        appliedControls: getAppliedControlsFromTemplate(t),
                        // Store additional template data
                        template: t
                    }));

                setWeeklyReports(weekly);
                setMonthlyReports(monthly);
            }
        } catch (error) {
            console.error('Error loading report templates:', error);
            // If API fails, fall back to empty arrays (or could add fallback to hard-coded data)
            setWeeklyReports([]);
            setMonthlyReports([]);
        } finally {
            setLoadingReportTemplates(false);
        }
    };

    const getAppliedControlsFromTemplate = (template) => {
        const controls = ['Report Period'];
        if (template.adjustable_start) controls.push('Starting Period');
        if (template.adjustable_end) controls.push('Ending Period');
        if (template.adjustable_retail_cost) controls.push('View Mode');
        if (template.adjustable_trend) controls.push('Sort');
        return controls;
    };

    const loadDefaultFilters = async () => {
        console.log(`📥 loadDefaultFilters called for client: ${activeClientId}`);
        const requestClientId = activeClientId;
        setLoadingDefaultFilters(true);
        try {
            const response = await axios.get(`${constants.BACKEND_DOMAIN}/api/filters`, {
                params: {
                    user_id: auth_context.user.id,
                    client_code: activeClientId,
                    type: 'global'
                }
            });

            if (activeClientIdRef.current !== requestClientId) {
                console.log(`Ignoring default filters response for stale client ${requestClientId}`);
                return;
            }

            if (response.data && Array.isArray(response.data)) {
                console.log(`✅ Loaded ${response.data.length} filters from API for client ${activeClientId}:`, response.data);

                // Map report names to their filter IDs
                const filterMap = {};
                const statusMap = {};
                const duplicateFilters = {}; // Track duplicates for cleanup

                response.data.forEach(filter => {
                    const reportKey = getReportKeyFromName(filter.report_name);

                    console.log(`Processing filter: report_name="${filter.report_name}", mapped to key="${reportKey}", filter.id=${filter.id}, updated_at=${filter.updated_at}`);

                    if (reportKey) {
                        // Check if we already have a filter for this report key (duplicate detection)
                        if (filterMap[reportKey]) {
                            // Duplicate found - keep the most recently updated one
                            const existingFilter = filterMap[reportKey];
                            const existingDate = new Date(existingFilter.updated_at);
                            const newDate = new Date(filter.updated_at);

                            const isOtbMonthVersionVariant = reportKey === 'otb_recap'
                                && existingFilter.month_version
                                && filter.month_version
                                && existingFilter.month_version !== filter.month_version;

                            if (isOtbMonthVersionVariant) {
                                const existingMatchesCurrentMode = existingFilter.month_version === otbRecapMonthVersion;
                                const newMatchesCurrentMode = filter.month_version === otbRecapMonthVersion;
                                let preferredFilter = existingFilter;

                                console.log(`ℹ️ OTB Recap returned multiple base filters by month_version: existing=${existingFilter.filterId}:${existingFilter.month_version}, incoming=${filter.id}:${filter.month_version}, currentMode=${otbRecapMonthVersion}`);

                                if (!existingMatchesCurrentMode && newMatchesCurrentMode) {
                                    console.log(`✓ Preferring OTB filter ${filter.id} because it matches current month mode ${otbRecapMonthVersion}`);
                                    preferredFilter = {
                                        filterId: filter.id,
                                        reportName: filter.report_name,
                                        ...filter
                                    };
                                    filterMap[reportKey] = preferredFilter;
                                } else {
                                    console.log(`✓ Keeping OTB filter ${existingFilter.filterId} because it matches current month mode ${otbRecapMonthVersion} better than ${filter.id}`);
                                }

                                if (
                                    preferredFilter.last_report_file?.id
                                    && preferredFilter.last_report_file?.status
                                    && doesFilterMatchParams(preferredFilter, buildFilterParamsForReport(reportKey))
                                ) {
                                    statusMap[reportKey] = preferredFilter.last_report_file;
                                    console.log(`  → Stored preferred OTB report status for ${reportKey}: file_id=${preferredFilter.last_report_file.id}, status=${preferredFilter.last_report_file.status}`);
                                }

                                return;
                            }

                            console.warn(`⚠️ Duplicate filter found for ${reportKey}: existing filter_id=${existingFilter.filterId}, new filter_id=${filter.id}`);

                            if (newDate > existingDate) {
                                // New filter is more recent, replace the old one
                                console.log(`✓ Using newer filter ${filter.id} (${filter.updated_at}) over ${existingFilter.filterId} (${existingFilter.updated_at})`);
                                duplicateFilters[reportKey] = existingFilter.filterId; // Track old filter

                                filterMap[reportKey] = {
                                    filterId: filter.id,
                                    reportName: filter.report_name,
                                    ...filter
                                };
                            } else {
                                // Existing filter is more recent, keep it
                                console.log(`✓ Keeping existing filter ${existingFilter.filterId} (${existingFilter.updated_at}) over ${filter.id} (${filter.updated_at})`);
                                duplicateFilters[reportKey] = filter.id; // Track the one we're skipping
                                return; // Skip this filter
                            }
                        } else {
                            // First filter for this report key
                            filterMap[reportKey] = {
                                filterId: filter.id,
                                reportName: filter.report_name,
                                ...filter
                            };
                        }

                        // Store last_report_file status - validate it has both id and status
                        // Only store if this is the filter we're keeping
                        if (filter.last_report_file && filterMap[reportKey].filterId === filter.id) {
                            // Ensure last_report_file has required properties
                            if (filter.last_report_file.id && filter.last_report_file.status) {
                                const shouldStoreStatus = reportKey !== 'otb_recap'
                                    || doesFilterMatchParams(filterMap[reportKey], buildFilterParamsForReport(reportKey));

                                if (shouldStoreStatus) {
                                    statusMap[reportKey] = filter.last_report_file;
                                    console.log(`  → Stored report status for ${reportKey}: file_id=${filter.last_report_file.id}, status=${filter.last_report_file.status}`);
                                } else {
                                    console.warn(`  → Skipping stale report status for ${reportKey} because filter params do not match current controls`, {
                                        filter: getTrackedFilterSnapshot(filterMap[reportKey]),
                                        expectedParams: buildFilterParamsForReport(reportKey),
                                        reportFile: filter.last_report_file,
                                    });
                                }
                            } else {
                                console.warn(`  → Invalid last_report_file for ${reportKey}:`, filter.last_report_file);
                            }
                        }
                    } else {
                        console.warn(`Could not map report_name "${filter.report_name}" to a report key`);
                    }
                });

                console.log('Final filterMap:', filterMap);
                console.log('Available filter keys:', Object.keys(filterMap));

                if (Object.keys(duplicateFilters).length > 0) {
                    console.error('⚠️ DUPLICATE FILTERS DETECTED AND RESOLVED:');
                    console.table(duplicateFilters);
                    console.warn('Multiple default filters exist for the same reports. This should be cleaned up in the database.');
                    console.warn('Recommend running: DELETE FROM filters WHERE id IN (' + Object.values(duplicateFilters).join(',') + ') AND type = "GLOBAL";');
                }

                setDefaultFilters(filterMap);
                setReportStatuses(statusMap);
            }
        } catch (error) {
            console.error('Error loading default filters:', error);
        } finally {
            setLoadingDefaultFilters(false);
        }
    };

    const getReportKeyFromName = (reportName) => {
        const mapping = {
            'otb_recap': 'otb_recap',
            'global_report': 'global_analytics',
            'inventory_report': 'inventory_action',
            'merchandise_report': 'merchandise_plan',
            'pod_report': 'pod_report',
            'progress': 'progress',
            'class_performance': 'class_performance',
            'inventory_performance': 'inventory_performance',
            'buyer_performance': 'buyer_performance',
            'inventory_variance': 'inventory_variance',
            'kpi_report': 'kpi_report'
        };
        return Object.keys(mapping).find(key => reportName === key) ? mapping[reportName] : null;
    };

    // Updates all default filters with current on-screen control values
    // Called when:
    // 1. Control values change (via useEffect)
    // 2. Client changes (via useEffect)
    // 3. Indirectly from handleStandardView (which calls the same clone logic inline for the specific report)
    const updateFiltersWithControls = async () => {
        console.log(`🔄 updateFiltersWithControls called for client: ${activeClientId}, filters count: ${Object.keys(defaultFilters).length}`);
        setUpdatingFilters(true);

        // Clear all report statuses since we're creating new filters
        // This prevents using stale file IDs from old filters
        setReportStatuses({});

        // Update each default filter with current control values
        for (const [reportKey, filterData] of Object.entries(defaultFilters)) {
            const config = reportControlConfig[reportKey];
            if (!config) continue;

            const params = {
                report_peroid: getReportPeriodFormatted(),
                sort: sortBy
            };

            // Add control-specific parameters based on report configuration
            if (config.adjustableRetailCost) {
                params.select = viewMode === 'Retail' ? 1 : 0;
            }

            if (config.adjustableStart) {
                // Calculate start_period as offset from report period
                // e.g., Report Period = Oct (9), Starting Period = Nov (10) -> offset = 1
                const reportMonth = reportPeriod.month;
                let startOffset;
                if (startingPeriod >= reportMonth) {
                    startOffset = startingPeriod - reportMonth;
                } else {
                    // Wrap around to next year
                    startOffset = (12 - reportMonth) + startingPeriod;
                }
                params.start_period = startOffset;
            }

            if (config.adjustableEnd) {
                // Calculate month_span_forward: number of months from starting period to ending period (inclusive)
                // This handles wrap-around (e.g., Oct to Sep next year)
                let monthSpan;
                if (endingPeriod >= startingPeriod) {
                    // Same year: Nov(10) to Dec(11) = 11 - 10 + 1 = 2 months
                    monthSpan = endingPeriod - startingPeriod + 1;
                } else {
                    // Wrap around case: Oct(9) to Sep(8) next year = (12 - 9) + 8 + 1 = 12 months
                    monthSpan = (12 - startingPeriod) + endingPeriod + 1;
                }

                // For OTB Recap and Inventory Action: use month_span_forward
                if (reportKey === 'otb_recap' || reportKey === 'inventory_action') {
                    params.month_span_forward = monthSpan;
                }

                // For Global Analytics: use ending_period (number of months from starting period to ending period)
                if (reportKey === 'global_analytics') {
                    // Global Analytics uses the same logic as other reports
                    params.ending_period = monthSpan;
                }
            }

            // Add month_version for OTB Recap
            if (reportKey === 'otb_recap') {
                params.month_version = otbRecapMonthVersion;
            }

            try {
                console.log(`Updating filter for ${reportKey} with params:`, params);

                // Clone/update the filter with new parameters
                const response = await axios.post(
                    `${constants.BACKEND_DOMAIN}/api/filters/${filterData.filterId}/clone`,
                    params,
                    {
                        params: {
                            user_id: auth_context.user.id,
                            client_code: activeClientId
                        }
                    }
                );

                console.log(`Filter ${reportKey} updated successfully:`, response.data);

                // Update the filter ID with the new cloned filter ID
                if (response.data && response.data.id) {
                    setDefaultFilters(prev => ({
                        ...prev,
                        [reportKey]: {
                            ...response.data,  // Spread the complete filter response from backend
                            filterId: response.data.id,  // Ensure filterId is set (the ID we use for API calls)
                            overwrite_filter_id: response.data.overwrite_filter_id,  // Preserve the base filter reference
                            // Note: response.data already contains all updated parameters
                        }
                    }));

                    // Check if the new filter already has a report file
                    if (response.data.last_report_file &&
                        response.data.last_report_file.id &&
                        response.data.last_report_file.status) {
                        setReportStatuses(prev => ({
                            ...prev,
                            [reportKey]: response.data.last_report_file
                        }));
                    }
                }
            } catch (error) {
                console.error(`Error updating filter for ${reportKey}:`, error);
            }
        }

        setUpdatingFilters(false);
    };

    const loadCustomReports = async () => {
        const requestClientId = activeClientId;
        const requestId = ++customReportsRequestRef.current;
        // String compare: the id flips from the URL's string to the context's number mid-load.
        const isStale = () => requestId !== customReportsRequestRef.current
            || String(activeClientIdRef.current) !== String(requestClientId);
        setLoadingCustomReports(true);
        try {
            const allCustomReports = [];

            // Try to load client-specific custom reports (new system - database-driven)
            // This will gracefully fail if the API doesn't exist yet
            try {
                const clientReportsResponse = await axios.get(`${constants.BACKEND_DOMAIN}/api/client-custom-reports`, {
                    params: {
                        client_id: requestClientId
                    }
                });

                if (clientReportsResponse.data && clientReportsResponse.data.custom_reports) {
                    if (activeClientIdRef.current !== requestClientId) {
                        return;
                    }
                    const clientReports = clientReportsResponse.data.custom_reports.map(report => {
                        // Store the icon name before it gets overwritten
                        const iconName = report.icon;
                        return {
                            ...report, // Spread first to get all properties
                            // Then override with our formatted properties
                            title: report.display_name,
                            label: 'client custom',
                            description: report.description || 'Custom report for this client',
                            icon: getIconComponent(iconName), // Map icon name to component AFTER spread
                            lastUpdated: report.updated_at || 'N/A',
                            appliedControls: ['Client Specific'],
                            reportKey: report.report_key,
                            componentName: report.component_name,
                            reportName: report.report_name,
                            isClientCustom: true, // Flag to distinguish from user-created custom filters
                            requiresEpochMonth: report.requires_epoch_month,
                            hasPdfDownload: report.has_pdf_download,
                            metadata: report.metadata,
                        };
                    });
                    allCustomReports.push(...clientReports);
                    console.log(`✅ Loaded ${clientReports.length} client-specific custom reports`);
                }
            } catch (error) {
                // Gracefully handle if new API doesn't exist yet (404) or other errors
                if (error.response?.status === 404) {
                    console.log('ℹ️ Client custom reports API not available yet (expected if migrations not run)');
                } else {
                    console.warn('Unable to load client custom reports:', error.message);
                }
                // Continue to load user-created custom filters
            }

            // Load user-created custom filters (existing system)
            try {
                if (auth_context.user?.id) {
                    const customFiltersResponse = await axios.get(`${constants.BACKEND_DOMAIN}/api/filters`, {
                        params: {
                            user_id: auth_context.user.id,
                            client_code: requestClientId,
                            type: 'customize'
                        }
                    });

                    if (isStale()) {
                        return;
                    }

                    if (customFiltersResponse.data && Array.isArray(customFiltersResponse.data)) {
                        const formattedFilters = customFiltersResponse.data.map(filter => ({
                            title: filter.filter_name,
                            label: 'custom level',
                            description: `${filter.report_display_name} - Custom filter set`,
                            icon: Settings,
                            lastUpdated: filter.updated_at || 'N/A',
                            appliedControls: getAppliedControls(filter),
                            filterId: filter.id,
                            reportName: filter.report_name,
                            isClientCustom: false, // User-created custom filter
                            // Store complete filter data for tracking
                            report_display_name: filter.report_display_name,
                            hierarchy: filter.hierarchy,
                            sort: filter.sort,
                            month_span: filter.month_span,
                            month_span_forward: filter.month_span_forward,
                            report_peroid: filter.report_peroid,
                            last_report_file: filter.last_report_file, // Include last_report_file
                            ...filter
                        }));
                        allCustomReports.push(...formattedFilters);

                        // Store last_report_file status for custom filter reports
                        const statusMap = {};
                        customFiltersResponse.data.forEach(filter => {
                            if (filter.last_report_file && filter.last_report_file.id && filter.last_report_file.status) {
                                statusMap[`custom-${filter.id}`] = filter.last_report_file;
                            } else if (filter.last_report_file) {
                                console.warn(`Invalid last_report_file for custom filter ${filter.id}:`, filter.last_report_file);
                            }
                        });
                        setReportStatuses(prev => ({ ...prev, ...statusMap }));
                    }

                }
            } catch (filterError) {
                console.warn('Unable to load user-created custom filters:', filterError.message);
            }

            // Ensure clients on a POS in constants.VENDOR_TOP_SELLERS_POS_IDS (mirrors
            // Client::VENDOR_TOP_SELLERS_POS_IDS) have Vendor Performance, Top Sellers and Aged Inventory.
            const hasVendor = allCustomReports.some(r => r.reportKey === 'vendor_performance' || r.componentName === 'VendorPerformanceReport' || r.title === 'Vendor Performance Analysis');
            const hasTop = allCustomReports.some(r => r.reportKey === 'top_sellers' || r.componentName === 'TopSellersReport' || r.title === 'Top Sellers / Item Performance');
            const hasAged = allCustomReports.some(r => r.reportKey === 'aged_inventory' || r.componentName === 'AgedInventoryReport' || r.title === 'Aged Inventory');
            const hasCoreReplenishment = allCustomReports.some(r => r.reportKey === 'core_replenishment' || r.componentName === 'CoreReplenishmentReport' || r.title === 'Core Item Replenishment');

            if (getsVendorTopSellersReports(client_context?.client)) {
                if (!hasVendor) {
                    allCustomReports.push({
                        title: 'Vendor Performance Analysis',
                        label: 'client custom',
                        description: 'Vendor performance analysis with sales, inventory, and sell-through metrics',
                        icon: BarChart3,
                        lastUpdated: 'N/A',
                        appliedControls: ['Client Specific'],
                        reportKey: 'vendor_performance',
                        componentName: 'VendorPerformanceReport',
                        reportName: 'vendor_performance',
                        isClientCustom: true,
                        requiresEpochMonth: false,
                        hasPdfDownload: false,
                    });
                }
                if (!hasTop) {
                    allCustomReports.push({
                        title: 'Top Sellers / Item Performance',
                        label: 'client custom',
                        description: 'Top performing items and variants ranked by sales, margin, or GMROI',
                        icon: TrendingUp,
                        lastUpdated: 'N/A',
                        appliedControls: ['Client Specific'],
                        reportKey: 'top_sellers',
                        componentName: 'TopSellersReport',
                        reportName: 'top_sellers',
                        isClientCustom: true,
                        requiresEpochMonth: false,
                        hasPdfDownload: false,
                    });
                }
                if (!hasAged) {
                    allCustomReports.push({
                        title: 'Aged Inventory',
                        label: 'client custom',
                        description: 'On-hand inventory aged from actual receive date, by style with per-SKU breakdown',
                        icon: Package,
                        lastUpdated: 'N/A',
                        appliedControls: ['Client Specific'],
                        reportKey: 'aged_inventory',
                        componentName: 'AgedInventoryReport',
                        reportName: 'aged_inventory',
                        isClientCustom: true,
                        requiresEpochMonth: false,
                        hasPdfDownload: false,
                    });
                }
                if (!hasCoreReplenishment) {
                    allCustomReports.push({
                        title: 'Core Item Replenishment',
                        label: 'client custom',
                        description: 'When each core item runs out against lead time plus cadence, units to reorder and the effect on OTB',
                        icon: Package,
                        lastUpdated: 'N/A',
                        appliedControls: ['Client Specific'],
                        reportKey: 'core_replenishment',
                        componentName: 'CoreReplenishmentReport',
                        reportName: 'core_replenishment',
                        isClientCustom: true,
                        requiresEpochMonth: false,
                        hasPdfDownload: false,
                    });
                }
            }

            if (isStale()) {
                return;
            }
            setCustomReports(allCustomReports);
            console.log(`📊 Total custom reports loaded: ${allCustomReports.length}`);
        } catch (error) {
            console.error('Error loading custom reports:', error);
        } finally {
            if (requestId === customReportsRequestRef.current) {
                setLoadingCustomReports(false);
            }
        }
    };

    // Helper function to map icon names to components
    const getIconComponent = (iconName) => {
        const iconMap = {
            'TrendingUp': TrendingUp,
            'FileText': FileText,
            'Package': Package,
            'BarChart3': BarChart3,
            'Target': Target,
            'Users': Users,
            'AlertTriangle': AlertTriangle,
            'Settings': Settings,
            'Download': Download,
            'Calendar': Calendar,
            'CheckCircle': CheckCircle,
        };
        return iconMap[iconName] || FileText;
    };


    const getAppliedControls = (filter) => {
        const controls = [];
        if (filter.report_peroid) controls.push('Report Period');
        if (filter.sort) controls.push('Sort');
        if (filter.select !== null) controls.push('View Mode');
        if (filter.start_period !== null) controls.push('Starting Period');
        if (filter.month_span_forward !== null || filter.ending_period !== null) controls.push('Ending Period');
        return controls;
    };

    const buildDownloadKey = (reportType, reportTitle) => `${reportType}-${reportTitle}`;

    const downloadGeneratedPdf = async ({ reportTitle, reportType, reportKey, filterId = null, currentStatusOverride = null }) => {
        const downloadKey = buildDownloadKey(reportType, reportTitle);

        const reportKeyStr = `${reportType}-${reportTitle}`;
        const statusKey = filterId ? `custom-${filterId}` : reportKey;
        const currentStatus = currentStatusOverride || reportStatuses[statusKey];
        const shouldAutoDownload = Boolean(pendingAutoDownloadsRef.current[statusKey]);
        const filterData = filterId
            ? customReports.find(r => r.filterId === filterId)
            : defaultFilters[reportKey];
        const expectedParams = !filterId && reportKey === 'otb_recap'
            ? buildFilterParamsForReport(reportKey)
            : null;

        if (!currentStatus?.id || currentStatus.id === 'undefined' || currentStatus.id === 'null') {
            console.error('Report status shows GENERATED but no valid file ID available. Status:', currentStatus);
            setReportStatuses(prev => {
                const newState = { ...prev };
                delete newState[statusKey];
                return newState;
            });
            await handleRegeneratePDF(reportTitle, reportType, reportKey, filterId, { autoDownload: shouldAutoDownload });
            return;
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const downloadUrl = `${constants.BACKEND_DOMAIN}/filter/${currentStatus.id}/download?user_id=${auth_context.user.id}&client_code=${activeClientId}&timezone=${timezone}`;

        if (reportKey === 'otb_recap' && !filterId) {
            logOtbRecapDownloadIntent({
                phase: shouldAutoDownload ? 'auto-download-ready' : 'download-click',
                filterData,
                expectedParams,
                currentStatus,
                downloadUrl,
            });

            if (!doesFilterMatchParams(filterData, expectedParams)) {
                console.warn('[OTB Recap PDF] Refusing to download stale generated PDF because the saved filter does not match the visible controls. Regenerating instead.', {
                    filterSnapshot: getTrackedFilterSnapshot(filterData),
                    expectedParams,
                    currentStatus,
                });

                setReportStatuses(prev => {
                    const newState = { ...prev };
                    delete newState[statusKey];
                    return newState;
                });

                await handleRegeneratePDF(reportTitle, reportType, reportKey, filterId, { autoDownload: shouldAutoDownload });
                return;
            }
        }

        console.log('Downloading PDF from URL:', downloadUrl);

        if (filterData) {
            const reportDisplayName = filterData.report_display_name || reportTitle;
            const now = new Date();
            const currentEpochMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

            trackEvent(`Report Created (${reportDisplayName})`, {
                generatedFrom: 'pdf',
                reportName: reportDisplayName,
                currentEpochMonth,
                clientId: activeClientId,
                clientCode: client_code,
                hierarchy: filterData.hierarchy || filterData.sort || sortBy,
                span: filterData.month_span || filterData.month_span_forward || endingPeriod,
                reportPeriod: filterData.report_peroid || getReportPeriodFormatted(),
            });
        }

        try {
            setDownloadingReports(prev => ({ ...prev, [downloadKey]: true }));
            console.log('[PDF Download] Starting blob download', {
                reportTitle,
                reportType,
                reportKey,
                statusKey,
                reportFileId: currentStatus.id,
                downloadUrl,
                autoDownload: shouldAutoDownload,
            });

            const response = await axios.get(downloadUrl, {
                responseType: 'blob',
                withCredentials: true,
                headers: {
                    'Accept': 'application/pdf',
                    'X-Requested-With': 'XMLHttpRequest',
                }
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = `${reportTitle}.pdf`;
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            delete pendingAutoDownloadsRef.current[statusKey];
            console.log('PDF downloaded successfully:', filename);
        } catch (error) {
            console.error('Error downloading PDF:', error);

            if (error.response) {
                console.error('Response status:', error.response.status);
                console.error('Response data:', error.response.data);

                if (error.response.status === 403) {
                    let errorMessage = 'Access denied';
                    let isWrongClientError = false;

                    if (error.response.data instanceof Blob) {
                        const text = await error.response.data.text();
                        try {
                            const json = JSON.parse(text);
                            errorMessage = json.error || 'Access denied';

                            if (typeof errorMessage === 'string' &&
                                (errorMessage.includes('different client') || errorMessage.includes('belongs to a different'))) {
                                isWrongClientError = true;
                            }
                        } catch (e) {
                            // Ignore parse error
                        }
                    } else if (error.response.data.error) {
                        errorMessage = error.response.data.error;
                        if (typeof errorMessage === 'string' &&
                            (errorMessage.includes('different client') || errorMessage.includes('belongs to a different'))) {
                            isWrongClientError = true;
                        }
                    }

                    if (isWrongClientError) {
                        console.log('Report belongs to different client, regenerating for current client...');
                        setReportStatuses(prev => {
                            const newState = { ...prev };
                            delete newState[statusKey];
                            return newState;
                        });
                        await handleRegeneratePDF(reportTitle, reportType, reportKey, filterId, { autoDownload: shouldAutoDownload });
                        return;
                    }

                    if (errorMessage.includes('storage.objects.get')) {
                        delete pendingAutoDownloadsRef.current[statusKey];
                        console.error('Google Cloud Storage Permission Error');
                        alert(`Unable to access PDF file due to Google Cloud Storage permissions.\n\nError: ${errorMessage}\n\nPlease contact your administrator to grant storage.objects.get permission to the service account.`);
                        return;
                    }

                    delete pendingAutoDownloadsRef.current[statusKey];
                    alert(`Access denied: ${errorMessage}`);
                    return;
                }

                // Handle 404 - PDF file not found, auto-generate it
                if (error.response.status === 404) {
                    console.log('PDF file not found in storage, triggering regeneration...');

                    // Parse error message from JSON response
                    let errorMessage = 'PDF not found';
                    let gcsError = null;

                    if (error.response.data instanceof Blob) {
                        const text = await error.response.data.text();
                        try {
                            const json = JSON.parse(text);
                            errorMessage = json.error || 'PDF not found';

                            // Check for nested GCS error
                            if (typeof json.error === 'object' && json.error.message) {
                                gcsError = json.error;
                                errorMessage = json.error.message;
                            }
                        } catch (e) {
                            // Ignore parse error
                        }
                    } else if (error.response.data.error) {
                        if (typeof error.response.data.error === 'object' && error.response.data.error.message) {
                            gcsError = error.response.data.error;
                            errorMessage = error.response.data.error.message;
                        } else {
                            errorMessage = error.response.data.error;
                        }
                    }

                    console.log('Error:', errorMessage);


                    // Clear the invalid status and regenerate
                    setReportStatuses(prev => {
                        const newState = { ...prev };
                        delete newState[statusKey];
                        return newState;
                    });

                    // Auto-generate the PDF
                    await handleRegeneratePDF(reportTitle, reportType, reportKey, filterId);
                    return;
                }

                delete pendingAutoDownloadsRef.current[statusKey];
                if (error.response.data instanceof Blob) {
                    const text = await error.response.data.text();
                    try {
                        const json = JSON.parse(text);
                        alert(`Download failed: ${json.error || 'Unknown error'}`);
                    } catch {
                        alert(`Download failed: ${error.response.status} - ${error.response.statusText}`);
                    }
                } else if (error.response.data.error) {
                    alert(`Download failed: ${error.response.data.error}`);
                } else {
                    alert(`Download failed: ${error.response.status} - ${error.response.statusText}`);
                }
            } else {
                delete pendingAutoDownloadsRef.current[statusKey];
                alert('Download failed. Please try again.');
            }
        } finally {
            setDownloadingReports(prev => ({ ...prev, [downloadKey]: false }));
        }
    };

    // Handle PDF download with loading state - calls backend PDF generation
    const handleDownloadPDF = async (reportTitle, reportType, reportKey, filterId = null) => {
        // Don't allow downloads while filters are being updated
        if (updatingFilters) {
            alert('Filters are being updated. Please wait a moment and try again.');
            return;
        }

        // Don't allow downloads while filters are still loading
        if (loadingDefaultFilters) {
            alert('Report data is still loading. Please wait a moment and try again.');
            return;
        }

        const statusKey = filterId ? `custom-${filterId}` : reportKey;
        const currentStatus = reportStatuses[statusKey];

        // Validate that we have the correct client context
        console.log(`Generating PDF for ${reportTitle} with activeClientId: ${activeClientId}, client_code: ${client_code}`);

        if (currentStatus && currentStatus.status === 'GENERATED') {
            pendingAutoDownloadsRef.current[statusKey] = {
                reportTitle,
                reportType,
                reportKey,
                filterId,
                clientId: activeClientId,
            };

            await downloadGeneratedPdf({
                reportTitle,
                reportType,
                reportKey,
                filterId,
                currentStatusOverride: currentStatus,
            });
            return;
        }

        // If currently building, show alert
        if (currentStatus && (currentStatus.status === 'BUILDING' || currentStatus.status === 'NOT_GENERATED')) {
            alert('Report is currently being generated. Please wait.');
            return;
        }

        // If no PDF available, automatically generate it instead of prompting
        await handleRegeneratePDF(reportTitle, reportType, reportKey, filterId, { autoDownload: true });
    };

    // Handle PDF Regeneration - forces new PDF generation
    const handleRegeneratePDF = async (reportTitle, reportType, reportKey, filterId = null, options = {}) => {
        const { autoDownload = false } = options;
        // Don't allow regeneration while filters are being updated
        if (updatingFilters) {
            if (autoDownload) {
                delete pendingAutoDownloadsRef.current[filterId ? `custom-${filterId}` : reportKey];
            }
            alert('Filters are being updated. Please wait a moment and try again.');
            return;
        }

        const reportKeyStr = `${reportType}-${reportTitle}`;
        const statusKey = filterId ? `custom-${filterId}` : reportKey;
        const currentStatus = reportStatuses[statusKey];
        console.log('Regenerating PDF for', reportKeyStr, 'with filterId', filterId);
        // If currently building, show alert
        if (currentStatus && (currentStatus.status === 'BUILDING' || currentStatus.status === 'NOT_GENERATED')) {
            if (autoDownload) {
                pendingAutoDownloadsRef.current[statusKey] = {
                    reportTitle,
                    reportType,
                    reportKey,
                    filterId,
                    clientId: activeClientId,
                };
            }
            alert('Report is currently being generated. Please wait.');
            return;
        }

        // Use the filter ID from defaultFilters for default reports, or passed filterId for custom
        let actualFilterId = filterId || (reportKey && defaultFilters[reportKey]?.filterId);

        if (!filterId && reportKey === 'otb_recap' && defaultFilters[reportKey]) {
            const expectedParams = buildFilterParamsForReport(reportKey);
            const currentFilter = defaultFilters[reportKey];

            logOtbRecapDownloadIntent({
                phase: 'regenerate-click',
                filterData: currentFilter,
                expectedParams,
                currentStatus,
            });

            if (!doesFilterMatchParams(currentFilter, expectedParams)) {
                console.warn('[OTB Recap PDF] Current filter is out of sync with visible controls before regeneration. Syncing filter first.', {
                    filterSnapshot: getTrackedFilterSnapshot(currentFilter),
                    expectedParams,
                });

                const updatedFilter = await cloneDefaultFilterForReport(reportKey, currentFilter, 'pre-regenerate-otb-sync');
                if (updatedFilter?.filterId) {
                    actualFilterId = updatedFilter.filterId;
                    setReportStatuses(prev => {
                        const newState = { ...prev };
                        delete newState[statusKey];
                        return newState;
                    });
                }
            }
        }

        if (!actualFilterId) {
            // For default reports without filter IDs, we need to create a filter first
            if (!filterId && reportKey) {
                if (autoDownload) {
                    delete pendingAutoDownloadsRef.current[statusKey];
                }
                console.error(`No filter found for ${reportKey}. This report has not been configured in the database.`);
                console.log('Available filters:', Object.keys(defaultFilters));
                console.log('Requested report key:', reportKey);

                alert(
                    `This report has not been set up yet for this client.\n\n` +
                    `Report: ${reportTitle}\n` +
                    `Key: ${reportKey}\n\n` +
                    `Please contact your administrator to initialize default filters for this client.`
                );
                return;
            } else if (isAdmin) {
                // For admins, show a different message indicating they can try force regeneration
                const confirmed = window.confirm('Filter is not ready yet. As an admin, do you want to force regeneration? This may fail if data is not finalized.');
                if (!confirmed) {
                    if (autoDownload) {
                        delete pendingAutoDownloadsRef.current[statusKey];
                    }
                    return;
                }
                if (autoDownload) {
                    delete pendingAutoDownloadsRef.current[statusKey];
                }
                alert('Unable to force regenerate without a valid filter. Please wait for the filter to load.');
                return;
            } else {
                if (autoDownload) {
                    delete pendingAutoDownloadsRef.current[statusKey];
                }
                alert('Please wait for report to load before regenerating.');
                return;
            }
        }

        if (autoDownload) {
            pendingAutoDownloadsRef.current[statusKey] = {
                reportTitle,
                reportType,
                reportKey,
                filterId,
                clientId: activeClientId,
            };
        }

        setReportStatuses(prev => ({
            ...prev,
            [statusKey]: {
                id: null,
                status: 'BUILDING'
            }
        }));

        // Final validation that actualFilterId is valid
        if (!actualFilterId || actualFilterId === 'undefined' || actualFilterId === 'null') {
            console.error('Invalid filter ID:', actualFilterId);
            if (autoDownload) {
                delete pendingAutoDownloadsRef.current[statusKey];
            }
            setReportStatuses(prev => {
                const newState = { ...prev };
                delete newState[statusKey];
                return newState;
            });
            alert('Unable to generate report: filter configuration is missing. Please refresh the page and try again.');
            return;
        }

        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const source = 'bq';

            console.log(`Starting PDF build for filter ID: ${actualFilterId}`);

            // For reports with filter IDs, trigger PDF generation
            // Add force=1 for admins to bypass finalization check
            const buildResponse = await axios.post(
                `${constants.BACKEND_DOMAIN}/api/filters/${actualFilterId}/start-building`,
                {},
                {
                    params: {
                        timezone: timezone,
                        source: source,
                        test_mode: 0,
                        force: isAdmin ? 1 : 0  // Admins can force regeneration
                    }
                }
            );

            // Update status with actual report file ID
            if (buildResponse.data) {
                setReportStatuses(prev => ({
                    ...prev,
                    [statusKey]: {
                        id: buildResponse.data.id,
                        status: 'BUILDING'
                    }
                }));

                // Poll for completion
                pollForPdfCompletion(actualFilterId, buildResponse.data.id, reportKeyStr, statusKey);
            }
        } catch (error) {
            console.error('Error regenerating PDF:', error);
            if (autoDownload) {
                delete pendingAutoDownloadsRef.current[statusKey];
            }
            setReportStatuses(prev => {
                const newState = { ...prev };
                delete newState[statusKey];
                return newState;
            });

            // Display the actual error message from the server
            const errorMessage = error.response?.data?.message || 'Error generating PDF. Please try again.';
            alert(errorMessage);
        }
    };

    // Handle PDF Cancellation - cancels an in-progress PDF generation
    const handleCancelPDF = async (reportTitle, reportType, reportKey, filterId = null) => {
        const statusKey = filterId ? `custom-${filterId}` : reportKey;
        const currentStatus = reportStatuses[statusKey];

        // Only allow canceling if status is BUILDING or NOT_GENERATED
        if (!currentStatus || (currentStatus.status !== 'BUILDING' && currentStatus.status !== 'NOT_GENERATED')) {
            alert('No generation in progress to cancel.');
            return;
        }

        // Validate that we have a valid report file ID
        if (!currentStatus.id || currentStatus.id === 'undefined' || currentStatus.id === 'null') {
            console.error('Cannot cancel: no valid report file ID. Status:', currentStatus);
            // Clear the invalid status
            setReportStatuses(prev => {
                const newState = { ...prev };
                delete newState[statusKey];
                return newState;
            });
            alert('Cannot cancel: invalid report file ID.');
            return;
        }

        const confirmCancel = window.confirm(`Are you sure you want to cancel the generation of "${reportTitle}"?`);
        if (!confirmCancel) {
            return;
        }

        try {
            console.log(`Canceling PDF generation for report file ID: ${currentStatus.id}`);

            // Call the cancel endpoint
            const response = await axios.post(
                `${constants.BACKEND_DOMAIN}/api/filter/report-files/${currentStatus.id}/cancel`,
                {},
                {
                    params: {
                        user_id: auth_context.user.id,
                        client_code: activeClientId
                    }
                }
            );

            console.log('PDF generation canceled successfully:', response.data);

            // Clear the report status to remove the building state
            setReportStatuses(prev => {
                const newState = { ...prev };
                delete newState[statusKey];
                return newState;
            });

            // Optional: Show success message
            // alert('PDF generation canceled successfully.');
        } catch (error) {
            console.error('Error canceling PDF generation:', error);

            // Display the actual error message from the server
            const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error canceling PDF generation. Please try again.';
            alert(errorMessage);

            // If the cancel failed because the job is already done, refresh the status
            if (error.response?.status === 400) {
                // Clear the status and let it reload
                setReportStatuses(prev => {
                    const newState = { ...prev };
                    delete newState[statusKey];
                    return newState;
                });
            }
        }
    };

    const pollForPdfCompletion = async (filterId, reportFileId, reportKey, statusKey) => {
        const maxAttempts = 60; // 5 minutes max
        let attempts = 0;
        const pollingClientId = activeClientId;

        const checkStatus = async () => {
            if (activeClientIdRef.current !== pollingClientId) {
                console.log(`Polling for ${statusKey} aborted: client changed from ${pollingClientId} to ${activeClientIdRef.current}`);
                return;
            }

            try {
                const response = await axios.get(
                    `${constants.BACKEND_DOMAIN}/api/filters/${filterId}/last-report-file`
                );

                if (response.data && response.data.status === 'GENERATED') {
                    if (activeClientIdRef.current !== pollingClientId) {
                        console.log(`Polling result for ${statusKey} discarded: client changed`);
                        return;
                    }
                    setReportStatuses(prev => ({
                        ...prev,
                        [statusKey]: response.data
                    }));
                    setDownloadingReports(prev => ({ ...prev, [reportKey]: false }));

                    const pendingAutoDownload = pendingAutoDownloadsRef.current[statusKey];
                    if (pendingAutoDownload && pendingAutoDownload.clientId === pollingClientId) {
                        await downloadGeneratedPdf({
                            reportTitle: pendingAutoDownload.reportTitle,
                            reportType: pendingAutoDownload.reportType,
                            reportKey: pendingAutoDownload.reportKey,
                            filterId: pendingAutoDownload.filterId,
                            currentStatusOverride: response.data,
                        });
                    }
                } else if (!response.data) {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkStatus, 5000);
                    } else {
                        delete pendingAutoDownloadsRef.current[statusKey];
                        setReportStatuses(prev => {
                            const newState = { ...prev };
                            delete newState[statusKey];
                            return newState;
                        });
                        setDownloadingReports(prev => ({ ...prev, [reportKey]: false }));
                        alert('PDF generation is taking longer than expected. Please check your downloads page.');
                    }
                } else if (response.data && response.data.status === 'FAILED') {
                    delete pendingAutoDownloadsRef.current[statusKey];
                    setReportStatuses(prev => {
                        const newState = { ...prev };
                        delete newState[statusKey];
                        return newState;
                    });
                    setDownloadingReports(prev => ({ ...prev, [reportKey]: false }));
                    alert('PDF generation failed. Please try again. If the issue continues, contact support.');
                } else if (response.data && (response.data.status === 'BUILDING' || response.data.status === 'NOT_GENERATED') && attempts < maxAttempts) {
                    attempts++;
                    setTimeout(checkStatus, 5000); // Check every 5 seconds
                } else {
                    delete pendingAutoDownloadsRef.current[statusKey];
                    setReportStatuses(prev => {
                        const newState = { ...prev };
                        delete newState[statusKey];
                        return newState;
                    });
                    setDownloadingReports(prev => ({ ...prev, [reportKey]: false }));
                    if (attempts >= maxAttempts) {
                        alert('PDF generation is taking longer than expected. Please check your downloads page.');
                    }
                }
            } catch (error) {
                console.error('Error checking PDF status:', error);
                delete pendingAutoDownloadsRef.current[statusKey];
                setReportStatuses(prev => {
                    const newState = { ...prev };
                    delete newState[statusKey];
                    return newState;
                });
                setDownloadingReports(prev => ({ ...prev, [reportKey]: false }));
            }
        };

        checkStatus();
    };

    // Handle Standard View - opens the web view from report library (replaces the old "Web View" button)
    const handleStandardView = async (reportTitle, reportType, reportKey, filterId = null) => {
        // Don't allow views while filters are being updated
        if (updatingFilters) {
            alert('Filters are being updated. Please wait a moment and try again.');
            return;
        }

        // Don't allow views while filters are still loading
        if (loadingDefaultFilters) {
            alert('Report data is still loading. Please wait a moment and try again.');
            return;
        }

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const source = 'bq';
        const is_dev = process.env.NODE_ENV === 'development' ? 1 : 0;

        // Use the filter ID from defaultFilters for default reports, or passed filterId for custom
        let actualFilterId = filterId || (reportKey && defaultFilters[reportKey]?.filterId);

        // For default reports (with reportKey), always update the filter with current on-screen values
        // to ensure the web view uses the latest control settings
        if (reportKey && defaultFilters[reportKey]) {
            const config = reportControlConfig[reportKey];
            if (config) {
                console.log(`🔄 Updating filter for ${reportKey} with current on-screen values before opening web view`);

                const params = {
                    report_peroid: getReportPeriodFormatted(),
                    sort: sortBy
                };

                // Add control-specific parameters based on report configuration
                if (config.adjustableRetailCost) {
                    params.select = viewMode === 'Retail' ? 1 : 0;
                }

                if (config.adjustableStart) {
                    // Calculate start_period as offset from report period
                    const reportMonth = reportPeriod.month;
                    let startOffset;
                    if (startingPeriod >= reportMonth) {
                        startOffset = startingPeriod - reportMonth;
                    } else {
                        // Wrap around to next year
                        startOffset = (12 - reportMonth) + startingPeriod;
                    }
                    params.start_period = startOffset;
                }

                if (config.adjustableEnd) {
                    // Calculate month_span_forward
                    let monthSpan;
                    if (endingPeriod >= startingPeriod) {
                        monthSpan = endingPeriod - startingPeriod + 1;
                    } else {
                        monthSpan = (12 - startingPeriod) + endingPeriod + 1;
                    }

                    if (reportKey === 'otb_recap' || reportKey === 'inventory_action') {
                        params.month_span_forward = monthSpan;
                    }

                    if (reportKey === 'global_analytics') {
                        params.ending_period = monthSpan;
                    }
                }

                // Add month_version for OTB Recap
                if (reportKey === 'otb_recap') {
                    params.month_version = otbRecapMonthVersion;
                }

                try {
                    const updatedFilter = await cloneDefaultFilterForReport(reportKey, defaultFilters[reportKey], 'standard-view-sync');

                    if (updatedFilter?.filterId) {
                        actualFilterId = updatedFilter.filterId;
                    }
                } catch (error) {
                    console.error(`Error updating filter for ${reportKey}:`, error);
                    alert('Failed to update report with current settings. Please try again.');
                    return;
                }
            }
        }

        // Validate that we have the correct client context
        console.log(`Opening Standard View for ${reportTitle} with activeClientId: ${activeClientId}, client_code: ${client_code}, filterId: ${actualFilterId}`);

        if (actualFilterId) {
            // Get filter data for tracking
            const filterData = filterId
                ? customReports.find(r => r.filterId === filterId)
                : defaultFilters[reportKey];

            // Calculate current epoch month (format: YYYYMM)
            const now = new Date();
            const currentEpochMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;

            // Track the event before opening the link (mimics filters.blade.php sendTrack function)
            const reportDisplayName = filterData?.report_display_name || reportTitle;
            trackEvent(`Report Created (${reportDisplayName})`, {
                generatedFrom: 'web view',
                reportName: reportDisplayName,
                currentEpochMonth: currentEpochMonth,
                clientId: activeClientId,
                clientCode: client_code,
                hierarchy: filterData?.hierarchy || filterData?.sort || sortBy,
                span: filterData?.month_span || filterData?.month_span_forward || endingPeriod,
                reportPeriod: filterData?.report_peroid || getReportPeriodFormatted(),
            });

            // Direct link to web view for specific filter
            const reportUrl = `${constants.BACKEND_DOMAIN}/filter/${actualFilterId}/web-view?user_id=${auth_context.user.id}&client_code=${activeClientId}&timezone=${timezone}&is_dev=${is_dev}&source=${source}`;
            window.open(reportUrl, '_blank');
        }
        // No else - if no filter ID, the button should be disabled
    };

    // Handle Dynamic View - opens the dynamic report tables
    const handleDynamicView = (reportTitle, reportType, componentName = null) => {
        // If componentName is provided (custom reports), use it directly
        if (componentName) {
            openDynamicModal(reportTitle, componentName);
            return;
        }

        // For all report titles, attempt to open dynamic modal
        // The DynamicModalContents component will handle rendering or return null if not supported
        openDynamicModal(reportTitle);
    };

    // Handle Create Custom Report - links to filters-form
    const handleCreateCustomReport = () => {
        // Navigate to the create custom report page within the app
        navigate(buildClientCustomReportCreatePath({
            clientCode: client_code,
            clientId: activeClientId,
            isV3: isDashboardV3View,
        }));
    };

    // Handle Edit Custom Report
    const handleEditCustomReport = (filterId, filterName) => {
        setOpenMenuId(null); // Close the menu
        navigate(buildClientCustomReportEditPath({
            clientCode: client_code,
            clientId: activeClientId,
            filterId,
            isV3: isDashboardV3View,
        }), {
            state: { filterName }
        });
    };

    // Handle Delete Custom Report - show confirmation modal
    const handleDeleteCustomReport = (filterId, filterName) => {
        setOpenMenuId(null); // Close the menu
        setDeleteConfirmation({ show: true, filterId, filterName });
    };

    // Confirm and execute delete
    const confirmDeleteCustomReport = async () => {
        const { filterId } = deleteConfirmation;

        try {
            // Call delete API using DELETE method
            await axios.delete(`${constants.BACKEND_DOMAIN}/api/filters/${filterId}`, {
                params: {
                    user_id: auth_context.user.id,
                    client_code: activeClientId
                }
            });

            // Close modal
            setDeleteConfirmation({ show: false, filterId: null, filterName: '' });

            // Trigger refresh to reload custom reports
            console.log('Custom report deleted successfully - refreshing list...');
            setRefreshCustomReports(prev => prev + 1);

        } catch (error) {
            console.error('Error deleting custom report:', error);

            // If it's a 404, the report was already deleted - treat as success but show message
            if (error.response?.status === 404) {
                // Close modal
                setDeleteConfirmation({ show: false, filterId: null, filterName: '' });

                // Show informative message
                const message = error.response?.data?.message || 'This custom report no longer exists.';
                alert(message);

                // Trigger refresh anyway to sync state
                setRefreshCustomReports(prev => prev + 1);
                return;
            }

            // For other errors, extract the user-friendly message
            const errorMessage = error.response?.data?.message
                || error.response?.data?.error
                || 'Failed to delete custom report. Please try again.';
            alert(errorMessage);
        }
    };

    // Cancel delete
    const cancelDeleteCustomReport = () => {
        setDeleteConfirmation({ show: false, filterId: null, filterName: '' });
    };

    // Toggle dropdown menu
    const toggleMenu = (filterId) => {
        setOpenMenuId(openMenuId === filterId ? null : filterId);
    };

    const getControlPills = (reportKey) => {
        // Try to find template from loaded reports
        const report = [...weeklyReports, ...monthlyReports].find(r => r.key === reportKey);
        if (report && report.template) {
            const pills = [report.template.label];
            if (report.template.adjustable_start) pills.push('adjustable start');
            if (report.template.adjustable_end) pills.push('adjustable end');
            if (report.template.adjustable_retail_cost) pills.push('adjustable retail/cost');
            if (report.template.adjustable_trend) pills.push('adjustable trend');
            return pills;
        }

        // Fallback to reportControlConfig if template not found
        const config = reportControlConfig[reportKey] || {};
        const pills = ['parent level']; // Default pill

        if (config.adjustableStart) pills.push('adjustable start');
        if (config.adjustableEnd) pills.push('adjustable end');
        if (config.adjustableRetailCost) pills.push('adjustable retail/cost');
        if (config.adjustableTrend) pills.push('adjustable trend');

        return pills;
    };

    const getDownloadIconState = (reportKey, reportType, filterId = null) => {
        const statusKey = filterId ? `custom-${filterId}` : reportKey;
        const status = reportStatuses[statusKey];

        if (!status) {
            // No report file yet - gray icon
            return 'gray';
        } else if (status.status === 'BUILDING') {
            // Building - spinner
            return 'building';
        } else if (status.status === 'GENERATED') {
            // Generated - green icon
            return 'generated';
        }
        return 'gray';
    };

    const currentReports = activeTab === 'weekly' ? weeklyReports : activeTab === 'monthly' ? monthlyReports : customReports;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Warning Banner for Missing Filters */}
            {missingFiltersCount > 0 && (
                <div className="bg-yellow-50 border-b border-yellow-200">
                    <div className="max-w-7xl mx-auto px-8 py-3">
                        <div className="flex items-center gap-2 text-yellow-800">
                            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium">
                                    {missingFiltersCount} report{missingFiltersCount > 1 ? 's are' : ' is'} not configured for PDF downloads
                                </p>
                                <p className="text-xs mt-0.5 text-yellow-700">
                                    Default filters need to be initialized for this client. Please contact your administrator.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Section */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-8 py-6">
                    {/* Title and Date */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-purple-50 border rounded-lg flex items-center justify-center">
                                <Calendar className="w-5 h-5 text-purple-700" />
                            </div>
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900">Report Controls</h1>
                                <p className="text-xs text-gray-500">Configure date range and view preferences for all reports</p>
                            </div>
                        </div>
                        <div className="text-right bg-gray-50 px-4 py-2 rounded-lg border">
                            <div className="text-xs font-medium text-gray-500 mb-0.5">DATA THROUGH DATE</div>
                            <div className="text-sm font-semibold text-gray-900">{dataThroughDate || '—'}</div>
                        </div>
                    </div>

                    {/* Controls Row */}
                    <div className="grid grid-cols-6 gap-3 mb-4">
                        {/* Report Period with Calendar Widget */}
                        <div className="relative" ref={calendarRef}>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Report Period</label>
                            <button
                                onClick={() => setShowCalendar(!showCalendar)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-left hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors flex items-center justify-between"
                            >
                                <span className="font-medium">{getMonthYearDisplay()}</span>
                                <Calendar className="w-4 h-4 text-gray-400" />
                            </button>

                            {/* Calendar Widget */}
                            {showCalendar && (
                                <div className="absolute z-50 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl p-4 w-72">
                                    {/* Year selector */}
                                    <div className="flex items-center justify-between mb-4">
                                        <button
                                            onClick={() => handleYearChange('down')}
                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                        >
                                            <span className="text-gray-600 text-sm">◀</span>
                                        </button>
                                        <span className="font-semibold text-gray-900">{reportPeriod.year}</span>
                                        <button
                                            onClick={() => handleYearChange('up')}
                                            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                                        >
                                            <span className="text-gray-600 text-sm">▶</span>
                                        </button>
                                    </div>

                                    {/* Month grid */}
                                    <div className="grid grid-cols-3 gap-2">
                                        {months.map((month, index) => (
                                            <button
                                                key={month}
                                                onClick={() => handleMonthSelect(index)}
                                                className={`px-3 py-2 text-xs font-medium rounded-md transition-colors ${reportPeriod.month === index
                                                    ? 'bg-purple-900 text-white'
                                                    : 'text-gray-700 hover:bg-purple-50'
                                                    }`}
                                            >
                                                {month.substring(0, 3)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Starting Period Dropdown */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Starting Period</label>
                            <select
                                value={startingPeriod}
                                onChange={(e) => setStartingPeriod(parseInt(e.target.value))}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                            >
                                {months.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                        </div>

                        {/* Ending Period Dropdown */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Ending Period</label>
                            <select
                                value={endingPeriod}
                                onChange={(e) => setEndingPeriod(parseInt(e.target.value))}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                            >
                                {months.map((month, index) => (
                                    <option key={month} value={index}>{month}</option>
                                ))}
                            </select>
                        </div>

                        {/* View Mode Dropdown */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">View Mode</label>
                            <select
                                value={viewMode}
                                onChange={(e) => setViewMode(e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                            >
                                <option value="Retail">Retail</option>
                                <option value="Cost">Cost</option>
                            </select>
                        </div>

                        {/* Sort Dropdown */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Sort</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                            >
                                <option value="CLASS">By Class</option>
                                <option value="EXECUTIVE">By Executive</option>
                                <option value="LOCATION">By Location</option>
                            </select>
                        </div>

                        {/* Actions Button */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Actions</label>
                            <button
                                onClick={handleCreateCustomReport}
                                className="w-full px-3 py-2 bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors flex items-center justify-center gap-1.5"
                            >
                                <span className="text-base">+</span>
                                <span>Create Custom Report</span>
                            </button>
                        </div>
                    </div>

                    {/* Summary Line */}
                    <div className="text-xs text-gray-600 mb-5 flex items-center justify-between bg-gray-50 px-4 py-2.5 rounded-md border">
                        <div className="flex items-center gap-4">
                            <span>Report Period: <span className="text-purple-700 font-semibold">{getMonthYearDisplay()}</span></span>
                            <span>View Mode: <span className="text-purple-700 font-semibold">{viewMode}</span></span>
                            <span>Sort: <span className="text-purple-700 font-semibold">{sortBy === 'CLASS' ? 'By Class' : sortBy === 'EXECUTIVE' ? 'By Executive' : 'By Location'}</span></span>
                        </div>
                        <span>Period Range: <span className="text-purple-700 font-semibold">{months[startingPeriod]} to {months[endingPeriod]}</span></span>
                    </div>

                    {/* Tabs */}
                    <div className="bg-gray-200 flex gap-2 w-full">
                        <button
                            onClick={() => setActiveTab('weekly')}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 rounded-lg font-semibold text-sm transition-all ${activeTab === 'weekly'
                                ? 'bg-purple-900 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            <TrendingUp className="w-4 h-4" />
                            <span>On Demand</span>
                            <span className={`ml-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'weekly' ? 'bg-white/20' : 'bg-gray-100'
                                }`}>{weeklyReports.length}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('monthly')}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 rounded-lg font-semibold text-sm transition-all ${activeTab === 'monthly'
                                ? 'bg-purple-900 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            <Calendar className="w-4 h-4" />
                            <span>Monthly Reports</span>
                            <span className={`ml-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'monthly' ? 'bg-white/20' : 'bg-gray-100'
                                }`}>{monthlyReports.length}</span>
                        </button>
                        <button
                            onClick={() => setActiveTab('custom')}
                            className={`flex-1 flex items-center justify-center gap-2 px-5 rounded-lg font-semibold text-sm transition-all ${activeTab === 'custom'
                                ? 'bg-purple-900 text-white shadow-sm'
                                : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                        >
                            <Settings className="w-4 h-4" />
                            <span>Custom Reports</span>
                            <span className={`ml-0.5 px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === 'custom' ? 'bg-white/20' : 'bg-gray-100'
                                }`}>{customReports.length}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Reports List */}
            <div className="max-w-7xl mx-auto px-8 py-6">
                {(loadingCustomReports && activeTab === 'custom') || (loadingDefaultFilters && activeTab !== 'custom') || loadingReportTemplates ? (
                    <div className="text-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-700" />
                        <p className="text-gray-600 mt-4">Loading reports...</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {currentReports.map((report, index) => {
                            const IconComponent = report.icon;
                            const reportKey = `${activeTab}-${report.title}`;
                            const isDownloading = downloadingReports[reportKey];
                            const controlPills = report.key ? getControlPills(report.key) : [report.label];
                            const iconState = getDownloadIconState(report.key, activeTab, report.filterId);

                            // Check if PDF is supported from template data
                            // For default reports (weekly/monthly), use has_pdf_download from template
                            // For custom reports, always support PDF
                            const supportsPDF = report.template?.has_pdf_download ||
                                (activeTab === 'custom' && report.reportName);

                            // Debug logging for Plan On Demand and Merchandise Plan
                            if (report.title === 'Plan On Demand' || report.title === 'Merchandise Plan') {
                                console.log(`${report.title}: supportsPDF=${supportsPDF}, has_pdf_download=${report.template?.has_pdf_download}`);
                            }

                            // Check if filter ID exists for this report
                            // Try multiple ways to find the filter ID:
                            // 1. Direct filterId from report (for custom reports)
                            // 2. Look up by report.key in defaultFilters
                            // 3. Try to find by matching report title to known mappings
                            let actualFilterId = report.filterId || (report.key && defaultFilters[report.key]?.filterId);

                            // Fallback: Try to match by title if key lookup failed
                            if (!actualFilterId && report.title) {
                                // Try to find filter by matching title
                                const filterEntry = Object.entries(defaultFilters).find(([key, filter]) => {
                                    const titleMatches = {
                                        'otb_recap': 'OTB Recap',
                                        'global_analytics': 'Global Analytics',
                                        'inventory_action': 'Inventory Action',
                                        'progress': 'Progress',
                                        'class_performance': 'Class Performance',
                                        'inventory_performance': 'Inventory Performance',
                                        'buyer_performance': 'Buyer Performance',
                                        'inventory_variance': 'Inventory Variance',
                                        'kpi_report': 'KPI Report',
                                        'merchandise_plan': 'Merchandise Plan',
                                        'pod_report': 'Plan On Demand'
                                    };
                                    return titleMatches[key] === report.title;
                                });
                                if (filterEntry) {
                                    actualFilterId = filterEntry[1].filterId;
                                }
                            }

                            // Debug logging (can be removed after debugging)
                            if (!actualFilterId) {
                                console.log(`Missing filter ID for report: ${report.title}, Key: ${report.key}, Available filters:`, Object.keys(defaultFilters));
                            }

                            // Always show View Report button, but PDF buttons only if filter ID exists and PDF is supported
                            // The View Report button will work even without filter ID by opening the dynamic modal
                            const hasFilterId = !!actualFilterId;


                            // Check if this is OTB Recap report
                            const isOtbRecap = report.key === 'otb_recap' || report.title === 'OTB Recap';

                            return (
                                <div key={index} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-4 flex-1">
                                            <div className="border w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                                                <IconComponent className="w-6 h-6 text-purple-700" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <h3 className="text-base font-semibold text-gray-900">{report.title}</h3>
                                                    {controlPills.map((pill, idx) => (
                                                        <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                                                            {pill}
                                                        </span>
                                                    ))}
                                                </div>
                                                <p className="text-sm text-gray-600 mb-1">{report.description}</p>

                                                {/* OTB Recap EOM/MTD Toggle */}
                                                {isOtbRecap && (
                                                    <div className="mt-2 flex items-center gap-2">
                                                        <span className="text-xs font-semibold text-gray-700">Time Range:</span>
                                                        <div className="inline-flex rounded-md shadow-sm" role="group">
                                                            <button
                                                                type="button"
                                                                onClick={() => setOtbRecapMonthVersion('MONTH_TO_DATE')}
                                                                className={`px-3 py-1 text-xs font-medium rounded-l-md border transition-colors ${otbRecapMonthVersion === 'MONTH_TO_DATE'
                                                                    ? 'bg-purple-900 text-white border-purple-900'
                                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                MTD
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setOtbRecapMonthVersion('MONTH_END')}
                                                                className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-r border-b transition-colors ${otbRecapMonthVersion === 'MONTH_END'
                                                                    ? 'bg-purple-900 text-white border-purple-900'
                                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                    }`}
                                                            >
                                                                EOM
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Single "View Report" Button - Purple background with eye icon */}
                                            {/* Use standard view (web view) for reports without dynamic view support */}
                                            {/* Use dynamic view for reports that support it */}
                                            <button
                                                onClick={() => {
                                                    // Force certain reports to always use Dynamic View
                                                    const alwaysDynamicReports = ['Monthly Export', 'Data Validation'];
                                                    const forceDynamic = alwaysDynamicReports.includes(report.title);

                                                    // Custom reports (activeTab === 'custom') should always use Dynamic View if they have a componentName
                                                    // For weekly/monthly reports, check if report has dynamic view support from template
                                                    const hasDynamicView = forceDynamic || (activeTab === 'custom'
                                                        ? !!report.componentName // Custom reports with componentName use dynamic view
                                                        : (report.template?.has_dynamic_view ?? true)); // Default/Weekly/Monthly check template

                                                    if (hasDynamicView) {
                                                        // Open dynamic modal view
                                                        // For custom reports, pass the componentName
                                                        handleDynamicView(report.title, activeTab, report.componentName);
                                                    } else {
                                                        // Open standard web view (PDF view)
                                                        handleStandardView(report.title, activeTab, report.key, report.filterId);
                                                    }
                                                }}
                                                disabled={!hasFilterId && activeTab === 'custom' && !report.componentName} // Custom reports without component require filter ID
                                                className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${!hasFilterId && activeTab === 'custom' && !report.componentName
                                                    ? 'bg-gray-400 text-white cursor-not-allowed opacity-50'
                                                    : 'bg-purple-900 text-white hover:bg-purple-800'
                                                    }`}
                                                title={
                                                    !hasFilterId && activeTab === 'custom' && !report.componentName
                                                        ? 'Report not configured - filter missing'
                                                        : 'View report'
                                                }
                                            >
                                                <Visibility sx={{ fontSize: 18 }} />
                                                <span>View Report</span>
                                            </button>
                                            {/* PDF Download Button - Only show for reports that support PDF and have filter ID */}
                                            {hasFilterId && supportsPDF && (
                                                <button
                                                    onClick={() => handleDownloadPDF(report.title, activeTab, report.key, report.filterId)}
                                                    disabled={iconState === 'building' || updatingFilters || isDownloading}
                                                    className={`p-2.5 bg-white border border-gray-300 rounded-lg transition-colors ${iconState === 'gray' || updatingFilters
                                                            ? 'text-gray-400 hover:bg-gray-50'
                                                            : iconState === 'building' || isDownloading
                                                                ? 'text-gray-700 cursor-not-allowed'
                                                                : 'text-green-600 hover:bg-green-50'
                                                        } ${(updatingFilters || isDownloading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    title={
                                                        updatingFilters
                                                            ? 'Updating filters...'
                                                            : isDownloading
                                                                ? 'Downloading PDF...'
                                                                : iconState === 'gray'
                                                                    ? 'Generate and Download PDF'
                                                                    : iconState === 'building'
                                                                        ? 'Generating PDF...'
                                                                        : 'Download PDF'
                                                    }
                                                >
                                                    {iconState === 'building' || updatingFilters || isDownloading ? (
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                    ) : (
                                                        <Download className="w-5 h-5" />
                                                    )}
                                                </button>
                                            )}
                                            {/* PDF Regenerate/Cancel Button - Only show for reports that support PDF and have filter ID */}
                                            {hasFilterId && supportsPDF && (
                                                <button
                                                    onClick={() => {
                                                        if (iconState === 'building') {
                                                            // Cancel the generation if currently building
                                                            handleCancelPDF(report.title, activeTab, report.key, report.filterId);
                                                        } else {
                                                            // Regenerate if not building
                                                            handleRegeneratePDF(report.title, activeTab, report.key, report.filterId);
                                                        }
                                                    }}
                                                    disabled={updatingFilters}
                                                    className={`p-2.5 bg-white border border-gray-300 rounded-lg transition-colors ${iconState === 'building'
                                                            ? 'text-red-600 hover:bg-red-50 border-red-300'
                                                            : iconState === 'gray' || updatingFilters
                                                                ? 'text-gray-400 hover:bg-gray-50'
                                                                : 'text-purple-600 hover:bg-purple-50'
                                                        } ${updatingFilters ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    title={
                                                        updatingFilters
                                                            ? 'Updating filters...'
                                                            : iconState === 'building'
                                                                ? 'Cancel PDF generation'
                                                                : iconState === 'gray'
                                                                    ? 'Regenerate PDF'
                                                                    : 'Regenerate PDF'
                                                    }
                                                >
                                                    {iconState === 'building' ? (
                                                        <X className="w-5 h-5" />
                                                    ) : (
                                                        <RefreshCw className="w-5 h-5" />
                                                    )}
                                                </button>
                                            )}
                                            {/* Three-dot Menu Button - Only for custom reports */}
                                            {activeTab === 'custom' && report.filterId && (
                                                <div className="relative dropdown-menu-container">
                                                    <button
                                                        onClick={() => toggleMenu(report.filterId)}
                                                        className="p-2.5 bg-white border border-gray-300 rounded-lg transition-colors text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                                                        title="More options"
                                                    >
                                                        <MoreVertical className="w-5 h-5" />
                                                    </button>
                                                    {/* Dropdown Menu */}
                                                    {openMenuId === report.filterId && (
                                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={() => handleEditCustomReport(report.filterId, report.title)}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                                                >
                                                                    <Edit className="w-4 h-4" />
                                                                    <span>Edit</span>
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteCustomReport(report.filterId, report.title)}
                                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {currentReports.length === 0 && activeTab === 'custom' && !loadingCustomReports && (
                            <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                                <Settings className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 mb-4">No custom reports yet</p>
                                <button
                                    onClick={handleCreateCustomReport}
                                    className="px-4 py-2 bg-purple-900 text-white text-sm font-semibold rounded-lg hover:bg-purple-800 transition-colors"
                                >
                                    Create Your First Custom Report
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Dynamic View Modal */}
            {showDynamicModal && (
                <ReportModal onClose={closeDynamicModal}>
                    <DynamicModalContents
                        reportTitle={dynamicReportTitle}
                        clientId={activeClientId}
                        onClose={closeDynamicModal}
                        monthVersion={otbRecapMonthVersion}
                        epochMonth={EpochMonthService.monthYearToEpochMonth(reportPeriod.month, reportPeriod.year)}
                        componentName={dynamicReportComponentName}
                    />
                </ReportModal>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmation.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4">
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                                    <Trash2 className="w-6 h-6 text-red-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Delete Custom Report</h3>
                                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                                </div>
                            </div>
                            <div className="mb-6">
                                <p className="text-gray-700 mb-2">
                                    Are you sure you want to delete this custom report?
                                </p>
                                <p className="text-sm font-medium text-gray-900 bg-gray-50 px-3 py-2 rounded border">
                                    {deleteConfirmation.filterName}
                                </p>
                            </div>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={cancelDeleteCustomReport}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteCustomReport}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2"
                                >
                                    <Trash2 className="w-4 h-4" />
                                    <span>Delete Report</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
